package no.vareflow.web

import no.vareflow.app.domain.*
import no.vareflow.app.sync.CloudMapping
import kotlin.random.Random

/**
 * Fordel arbeid: first the list of people; tap a name to tick which Hovedvarer the person counts
 * (or switch to Hovedvarer and tick people per Hovedvare). New people can be added from here.
 * Nothing is saved before Lagre.
 */
fun WebApp.showAssignments(session: InventorySession, admin: UserAccount) {
    val state = object {
        /** "people", "person", "categories", "category" or "new". */
        var view = "people"
        var personId: String? = null
        var category: String? = null
        val draft: MutableMap<String, MutableSet<String>> = core.orderedCategories()
            .associateWith { (core.assignment(session.id, it)?.assigneeIds ?: emptySet()).toMutableSet() }
            .toMutableMap()
        var asDefault = true
        var newName = ""
        var newEmail = ""
        var newRole = Role.EMPLOYEE
        var error: String? = null
        /** Start code of a person created in this dialog (local test mode only). */
        var startCode: Pair<String, String>? = null
        /** Firebase: what to tell the admin after creating a person (title, text). */
        var notice: Pair<String, String>? = null
        var withoutEmail = false
        /** Time-limited helper (Firmakode + brukernavn + startkode, no own password). */
        var helper = false
        var helperHours = HelperAccess.DEFAULT_HOURS
        /** The helper just created: QR code and start code to hand over. */
        var helperLogin: HelperLogin? = null
        var newUsername = ""
        var busy = false
    }
    fun people() = core.users.filter { it.active && it.role != Role.ADMIN }.sortedWith { a, b -> compareNorwegian(a.name, b.name) }
    fun categoriesOf(id: String) = core.orderedCategories().filter { id in state.draft[it].orEmpty() }
    fun go(view: String) { state.view = view; state.error = null; requestRender() }

    openDialog(t("Fordel arbeid", "Assign work")) { close ->
        p("${formatYearMonth(session.yearMonth)} · ${session.locationName}", "muted small")
        if (state.view == "people" || state.view == "categories") {
            div("sub-tabs") {
                listOf("people" to t("Personer", "People"), "categories" to t("Hovedvarer", "Main categories")).forEach { (key, label) ->
                    val selected = state.view == key
                    el("button", "sub-tab" + if (selected) " selected" else "", label) {
                        attr("type", "button")
                        onClick { if (!selected) go(key) }
                    }
                }
            }
        }
        state.notice?.let { (title, text) ->
            div("invite-note") {
                attr("role", "status")
                div("strong", title)
                text.split('\n').forEach { line -> div("small", line) }
                state.helperLogin?.let { helperLoginCard(this, it) }
            }
        }
        state.startCode?.let { (name, code) ->
            div("invite-note") {
                attr("role", "status")
                div("strong", t("$name er lagt til.", "$name has been added."))
                div("small", t("Startkode for innlogging: $code", "Start code for login: $code"))
                div("muted tiny", t("Testmodus: koden vises bare nå.", "Test mode: the code is shown only now."))
            }
        }
        when (state.view) {
            "people" -> {
                val list = people()
                if (list.isEmpty()) p(t("Ingen personer ennå.", "No people yet."), "muted small")
                div("picker-list") {
                    list.forEach { person ->
                        val mine = categoriesOf(person.id)
                        el("button", "picker-item") {
                            attr("type", "button")
                            div("avatar", person.name.trim().take(1).uppercase())
                            div("grow") {
                                div("strong", person.name)
                                div("muted small", if (mine.isEmpty()) t("Ingen hovedvarer", "No main categories") else mine.joinToString(", "))
                                helperStatusText(person.accessUntil)?.let { div("muted tiny", it) }
                            }
                            span("chevron", "›")
                            onClick { state.personId = person.id; go("person") }
                        }
                    }
                }
                if (admin.role == Role.ADMIN || AccessRules.hasPermission(admin, Permission.USER_MANAGE)) button(t("+ Ny person", "+ New person"), "btn outline block") {
                    state.newName = ""; state.newEmail = ""; state.newRole = Role.EMPLOYEE; state.helper = false; go("new")
                }
            }
            "person" -> {
                val person = core.userById(state.personId ?: "")
                if (person == null) { go("people"); return@openDialog }
                button(t("‹ Personer", "‹ People"), "btn text back") { go("people") }
                h2(person.name, "dialog-subtitle")
                p(t("Kryss av hovedvarene ${person.name} skal telle og se.", "Tick the main categories ${person.name} should count and see."), "muted small")
                val chosen = categoriesOf(person.id).toSet()
                div("row") {
                    button(t("Velg alle", "Select all"), "btn text small") { state.draft.values.forEach { it.add(person.id) }; requestRender() }
                    button(t("Ingen", "None"), "btn text small") { state.draft.values.forEach { it.remove(person.id) }; requestRender() }
                }
                div("check-list") {
                    core.orderedCategories().forEach { category ->
                        checkbox(category, category in chosen, "as-${person.id}-$category") { checked ->
                            val ids = state.draft.getOrPut(category) { mutableSetOf() }
                            if (checked) ids.add(person.id) else ids.remove(person.id)
                            requestRender()
                        }
                    }
                }
            }
            "categories" -> {
                div("picker-list") {
                    core.orderedCategories().forEach { category ->
                        val names = state.draft[category].orEmpty().mapNotNull { core.userById(it)?.name }
                        el("button", "picker-item") {
                            attr("type", "button")
                            div("grow") {
                                div("strong", category)
                                div("muted small", if (names.isEmpty()) t("Ikke tildelt", "Not assigned") else names.joinToString(", "))
                            }
                            span("chevron", "›")
                            onClick { state.category = category; go("category") }
                        }
                    }
                }
            }
            "category" -> {
                val category = state.category ?: run { go("categories"); return@openDialog }
                button(t("‹ Hovedvarer", "‹ Main categories"), "btn text back") { go("categories") }
                h2(category, "dialog-subtitle")
                val ids = state.draft.getOrPut(category) { mutableSetOf() }
                div("check-list") {
                    people().forEach { person ->
                        checkbox(person.name, person.id in ids, "ac-$category-${person.id}") { checked ->
                            if (checked) ids.add(person.id) else ids.remove(person.id)
                            requestRender()
                        }
                    }
                }
            }
            "new" -> {
                button(t("‹ Personer", "‹ People"), "btn text back") { go("people") }
                h2(t("Ny person", "New person"), "dialog-subtitle")
                field(t("Navn", "Name"), state.newName, "np-name") { state.newName = it }
                if (cloudMode) {
                    div("chip-row wrap") {
                        chip(t("Med e-post", "With e-mail"), !state.withoutEmail && !state.helper) { state.withoutEmail = false; state.helper = false; state.error = null; requestRender() }
                        chip(t("Uten e-post", "Without e-mail"), state.withoutEmail && !state.helper) { state.withoutEmail = true; state.helper = false; state.error = null; requestRender() }
                        chip(t("Hjelper (tidsbegrenset)", "Helper (time-limited)"), state.helper) {
                            state.helper = true; state.withoutEmail = true; state.newRole = Role.EMPLOYEE; state.error = null; requestRender()
                        }
                    }
                }
                if (cloudMode && state.withoutEmail) {
                    val normalized = CompanyLogin.normalizeUsername(state.newUsername)
                    field(t("Brukernavn", "Username"), state.newUsername, "np-username", autocomplete = "off",
                        hint = if (normalized.isNotBlank() && normalized != state.newUsername.trim()) t("Blir: $normalized", "Becomes: $normalized") else t("Til innlogging med Firmakode", "For login with company code")) { state.newUsername = it }
                } else {
                    field(t("E-post", "E-mail"), state.newEmail, "np-email", type = "email", autocomplete = "off",
                        hint = t("Brukes til innlogging. Invitasjonen sendes hit.", "Used for login. The invitation is sent here.")) { state.newEmail = it }
                }
                if (cloudMode && state.helper) {
                    div("strong small", t("Tilgang i", "Access for"))
                    helperDurationChips(state.helperHours) { state.helperHours = it; requestRender() }
                    p(t("Hjelperen logger inn med Firmakode, brukernavn og startkode (QR-kode eller lenke) og kan telle de hovedvarene du velger. Etter tiden stenges tilgangen av seg selv – du kan forlenge den under Brukere og tilgang.",
                        "The helper logs in with company code, username and start code (QR code or link) and can count the main categories you choose. When the time is up, access closes by itself – you can extend it under Users and access."), "muted tiny")
                } else div("chip-row") {
                    chip(t("Ansatt", "Employee"), state.newRole == Role.EMPLOYEE) { state.newRole = Role.EMPLOYEE; requestRender() }
                    chip(t("Leder", "Leader"), state.newRole == Role.LEADER) { state.newRole = Role.LEADER; requestRender() }
                    // Only an admin may make another admin (also enforced by the server rules).
                    if (cloudMode && admin.role == Role.ADMIN) chip("Admin", state.newRole == Role.ADMIN) { state.newRole = Role.ADMIN; requestRender() }
                }
                if (!(cloudMode && state.helper)) p(if (cloudMode && state.withoutEmail) t("Du får en startkode å gi videre sammen med Firmakoden. Første gang velger personen sitt eget passord.", "You get a start code to pass on together with the company code. The first time, the person chooses their own password.")
                  else t("Du trenger ikke vite passordet: personen velger det selv.", "You don't need to know the password: the person chooses it."), "muted tiny")
                errorText(state.error)
                button(if (state.busy) t("Oppretter …", "Creating …") else t("Legg til og velg hovedvarer", "Add and choose main categories"), "btn primary block", enabled = !state.busy) {
                    val name = state.newName.trim()
                    if (cloudMode) {
                        val role = if (state.helper) Role.EMPLOYEE else state.newRole
                        createCloudPerson(name, state.newEmail.trim(), state.newUsername, role, state.withoutEmail,
                            onError = { state.error = it; requestRender() },
                            onBusy = { state.busy = it; requestRender() },
                            accessUntil = if (state.helper) HelperAccess.until(nowMillis(), state.helperHours) else null
                        ) { personId, title, text, helperLogin ->
                            state.notice = title to text
                            state.helperLogin = helperLogin
                            state.personId = personId
                            // An admin sees everything: no Hovedvarer to choose.
                            go(if (state.newRole == Role.ADMIN) "people" else "person")
                        }
                        return@button
                    }
                    val email = state.newEmail.trim()
                    state.error = when {
                        name.isBlank() || email.isBlank() -> t("Fyll ut navn og e-post.", "Fill in name and e-mail.")
                        !Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$").matches(email) -> t("Skriv inn en gyldig e-postadresse.", "Enter a valid e-mail address.")
                        core.users.any { it.username.equals(email, ignoreCase = true) } -> t("E-postadressen er allerede i bruk.", "The e-mail address is already in use.")
                        else -> null
                    }
                    if (state.error != null) { requestRender(); return@button }
                    val code = (0 until 6).map { Random.nextInt(10) }.joinToString("")
                    val account = UserAccount(
                        name = name, username = email, password = code, role = state.newRole,
                        allowedCategories = emptySet(), permissions = defaultPermissions(state.newRole)
                    )
                    core.addUser(account, admin)
                    state.startCode = name to code
                    state.personId = account.id
                    go("person")
                }
            }
        }
        if (state.view != "new") {
            checkbox(t("Gjelder også andre åpne måneder og nye måneder", "Also applies to other open months and new months"), state.asDefault, "as-default") { state.asDefault = it }
        }
        dialogActions {
            button(t("Avbryt", "Cancel"), "btn text") { close() }
            button(t("Lagre", "Save"), "btn primary") {
                state.draft.forEach { (category, ids) ->
                    if (core.assignment(session.id, category)?.assigneeIds != ids) core.setCategoryAssignees(session.id, category, ids.toSet(), admin)
                }
                if (state.asDefault) {
                    people().forEach { person ->
                        val mine = categoriesOf(person.id).toSet()
                        if (person.allowedCategories != mine) core.updateUser(person.copy(allowedCategories = mine), admin)
                    }
                }
                close()
                toast(t("Arbeidsfordelingen er lagret.", "The work assignment is saved."))
            }
        }
    }
}

/**
 * Firebase: a new person with e-mail (account with a random password nobody sees + Firebase's
 * "choose your password" e-mail) or without e-mail (brukernavn + start code, own password at the
 * first login). The membership is added here at once; the listener confirms it.
 */
internal fun WebApp.createCloudPerson(
    name: String,
    email: String,
    rawUsername: String,
    role: Role,
    withoutEmail: Boolean,
    onError: (String) -> Unit,
    onBusy: (Boolean) -> Unit,
    /** Set for a time-limited helper (always without e-mail, never asked for an own password). */
    accessUntil: Long? = null,
    onCreated: (personId: String, title: String, text: String, helper: HelperLogin?) -> Unit
) {
    val membership = cloud.membership ?: return onError(t("Logg inn på nytt.", "Log in again."))
    val username = CompanyLogin.normalizeUsername(rawUsername)
    val login = if (withoutEmail) CompanyLogin.syntheticEmail(username, membership.companyId) else email
    val problem = when {
        name.isBlank() -> t("Fyll ut navn.", "Fill in the name.")
        withoutEmail && username.isBlank() -> t("Fyll ut brukernavn.", "Fill in the username.")
        withoutEmail -> CompanyLogin.usernameProblem(username)
        email.isBlank() || !Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$").matches(email) -> t("Skriv inn en gyldig e-postadresse.", "Enter a valid e-mail address.")
        else -> null
    } ?: if (core.users.any { it.username.equals(login, ignoreCase = true) }) {
        if (withoutEmail) t("Brukernavnet er allerede i bruk.", "The username is already in use.") else t("E-postadressen er allerede i bruk.", "The e-mail address is already in use.")
    } else null
    if (problem != null) return onError(problem)
    val account = UserAccount(name = name, username = login, password = "", role = role,
        allowedCategories = emptySet(), permissions = defaultPermissions(role), accessUntil = accessUntil)
    val password = if (withoutEmail) CompanyLogin.startCode(Random(secureSeed()))
        else secureRandomString(24, "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789")
    onBusy(true)
    // A helper keeps the start code as password (the time limit closes the account anyway).
    cloud.createCompanyUser(account, password, mustChangePassword = withoutEmail && accessUntil == null) { result ->
        result.onSuccess { uid ->
            core.applyCloudMemberships(listOf(
                CloudMapping.membershipToCloud(account.copy(id = uid), membership.locationId, membership.locationName, nowMillis()) + ("id" to uid)
            ))
            if (withoutEmail && accessUntil != null) {
                onBusy(false)
                onCreated(uid, t("$name er lagt til som hjelper.", "$name has been added as a helper."),
                    t("Gi QR-koden eller lenken, og startkoden separat (vises bare nå).",
                        "Give the QR code or link, and the start code separately (shown only now)."),
                    HelperLogin(membership.companyCode, username, password, accessUntil))
            } else if (withoutEmail) {
                onBusy(false)
                onCreated(uid, t("$name er lagt til.", "$name has been added."), buildString {
                    append(t("Gi $name dette (vises bare nå):", "Give $name this (shown only now):") + "\n")
                    if (membership.companyCode.isNotBlank()) append(t("Firmakode", "Company code") + ": ${membership.companyCode}\n")
                    append(t("Brukernavn", "Username") + ": $username\n" + t("Startkode", "Start code") + ": $password\n")
                    append(t("Logg inn → Firmakode. Første gang velger $name sitt eget passord.", "Log in → Company code. The first time, $name chooses their own password."))
                }, null)
            } else {
                cloud.sendInviteEmail(email) { sent ->
                    onBusy(false)
                    onCreated(uid, t("$name er lagt til.", "$name has been added."),
                        if (sent.isSuccess) t("Invitasjon sendt til $email.", "Invitation sent to $email.")
                        else t("Kontoen er opprettet, men e-posten kunne ikke sendes: ", "The account was created, but the e-mail could not be sent: ") + sent.exceptionOrNull()?.message.orEmpty(),
                        null)
                }
            }
        }.onFailure {
            onBusy(false)
            onError(t("Feil", "Error") + ": " + (it.message ?: t("kunne ikke opprette personen", "could not create the person")))
        }
    }
}
